/**
 * /admin/reports/profit-loss — Profit & Loss.
 * @file src/web/features/admin/pages/reports/profit-loss.tsx
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, TrendingUp, TrendingDown, FileText } from 'lucide-react';
import { SEO } from '../../../../shared/components';
import { AdminPageHeader } from '../../components/AdminPageHeader';
import { PageLoading } from '../../components/PageLoading';
import { adminFetchJson } from '../../lib/admin-api';
import { useCompany } from '../../lib/company-context';
import { usePeriod } from '../../lib/period-context';
import { PeriodBar } from '../../components/PeriodBar';

interface Block { name: string; amount: number; ledgers: { id: string; name: string; amount: number }[] }
interface PL { income: Block[]; expense: Block[]; totals: { income: number; expense: number; netProfit: number } }

const inr = (n: number) => (n ? n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00');

export default function ProfitLossPage() {
  const { entityId } = useCompany();
  const { from, to } = usePeriod();
  const [data, setData] = useState<PL | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false; setData(null); setError(null);
    adminFetchJson<PL>('/api/tally-import/profit-loss', { query: { entityId: entityId ?? undefined, from: from || undefined, to: to || undefined } })
      .then((d) => !cancel && setData(d))
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => { cancel = true; };
  }, [entityId, from, to]);

  const empty = data && data.income.length === 0 && data.expense.length === 0;
  const profit = (data?.totals.netProfit ?? 0) >= 0;

  return (
    <>
      <SEO title="Profit & Loss" />
      <AdminPageHeader title="Profit & Loss" breadcrumbs={[{ label: 'Reports', href: '/admin/reports' }, { label: 'Profit & Loss' }]} />
      <Link to="/admin/reports" className="mb-4 inline-flex items-center gap-1 text-sm text-ink-500 no-underline hover:text-ink-700"><ChevronLeft className="h-4 w-4" /> All reports</Link>
      <PeriodBar />

      {error && <div className="rounded-2xl border border-danger-200 bg-danger-50 px-6 py-4 text-sm text-danger-700">Failed to load: {error}</div>}
      {!data && !error && <PageLoading label="Building profit & loss" />}
      {empty && (
        <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-16 text-center">
          <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-ink-100 text-ink-400"><FileText className="h-6 w-6" /></span>
          <h3 className="mt-5 text-lg font-semibold text-ink-900">Nothing to show</h3>
          <p className="mt-1.5 text-sm text-ink-600">No income or expenses in this period.</p>
        </div>
      )}

      {data && !empty && (
        <div className="space-y-5">
          <Section title="Income" blocks={data.income} total={data.totals.income} />
          <Section title="Expenses" blocks={data.expense} total={data.totals.expense} />

          <div className={`flex items-center justify-between rounded-2xl border p-5 ${profit ? 'border-ok-200 bg-ok-50' : 'border-danger-200 bg-danger-50'}`}>
            <div className="flex items-center gap-3">
              <span className={`grid size-10 place-items-center rounded-xl ${profit ? 'bg-ok-100 text-ok-700' : 'bg-danger-100 text-danger-700'}`}>
                {profit ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
              </span>
              <span className={`text-sm font-semibold ${profit ? 'text-ok-800' : 'text-danger-800'}`}>{profit ? 'Net Profit' : 'Net Loss'}</span>
            </div>
            <span className={`text-xl font-bold tabular-nums ${profit ? 'text-ok-800' : 'text-danger-800'}`}>₹ {inr(Math.abs(data.totals.netProfit))}</span>
          </div>
        </div>
      )}
    </>
  );
}

function Section({ title, blocks, total }: { title: string; blocks: Block[]; total: number }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
      <div className="border-b border-ink-100 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-ink-400">{title}</div>
      <table className="w-full text-sm">
        <tbody>
          {blocks.length === 0 && <tr><td className="px-5 py-4 text-ink-400">None</td></tr>}
          {blocks.map((b) => (
            <tr key={b.name} className="border-t border-ink-50">
              <td className="px-5 py-2.5 font-medium text-ink-800">{b.name}</td>
              <td className="px-5 py-2.5 text-right tabular-nums text-ink-800">{inr(b.amount)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-ink-200 bg-ink-50 font-semibold text-ink-900">
            <td className="px-5 py-3">Total {title}</td>
            <td className="px-5 py-3 text-right tabular-nums">{inr(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
